/**
 * Vendor trading panel (design/gdd/crafting-economy.md's Economy half).
 *
 * ─── Two blocks and a trade mark (2026-09-16) ──────────────────────────────
 * Janeks: *"Split Player's inventory and the trader's inventory into 2 blocks.
 * Between them a trade icon (half arrows both ways in a circle, grungy)"*. The
 * two sides used to be bare `<div style="flex:1">` children of one flex row
 * with no frame of any kind, so they read as two lists on one surface. They
 * are now `.bv-block` frames either side of a real grid column holding the
 * mark, which is why the mark stays BETWEEN them at every width instead of
 * drifting to one side; at the stacked breakpoint it rotates a quarter turn,
 * because a divider between two stacked blocks has to be horizontal.
 *
 * ─── Slots, shared with the bag ────────────────────────────────────────────
 * Both sides render through `ui/item-slot.ts` — the same square well, hover
 * overlay and tooltip the paperdoll uses — so an item looks and behaves
 * identically whether you are carrying it or buying it. That also settles the
 * "with nothing to sell, still show the empty grid" ask by construction: the
 * grid is drawn from a slot COUNT, and an empty pool is a grid of empty slots,
 * not a sentence where a grid should be.
 *
 * ─── Prices on the sell side come from the server ──────────────────────────
 * F3 depends on `EconomyConfig` (sellRate, categoryMatchMult, minSellPrice),
 * which is admin-tunable and cached, so this panel READS prices from
 * `GET /vendor/:key/quote` rather than recomputing the formula. A client-side
 * copy would quote numbers the server stops honouring the moment that config
 * is tuned, and would do it silently.
 *
 * ─── Capacity is a gate, not an error message ──────────────────────────────
 * Purchases land in the HIDEOUT. The server enforces its cap and throws
 * `HIDEOUT_FULL`, which used to surface as red text AFTER the click. Buy is
 * now disabled up front, with the reason in the tooltip. Sell is NEVER
 * disabled by capacity — selling is the player's only way out of a full
 * hideout, and a panel that locks both directions is a trap.
 *
 * Modeled on world-map-ui.ts's factory-function pattern (create / open / close
 * / dispose, backdrop-click + Escape to close).
 */
import { gameStateStore } from '../../state/game-state';
import type { InventoryItemSummary } from '../../state/game-state.types';
import {
  vendorStateStore, sellItems, buyItem, closeVendor, loadVendor,
} from '../../state/vendor-state';
import type { VendorState, VendorStockEntry } from '../../state/vendor-state.types';
import { pictureHtml } from './paperdoll';
import { cashCoinHtml } from './coin';
import { capacityLevel, fillPercent, worstLevel } from './capacity';
import {
  emptySlotHtml, gridSlotCount, slotGridStyle, slotHtml, type SlotAction,
} from './item-slot';
import { inventoryTooltipHtml, ItemTooltip, itemTooltipHtml } from './item-tooltip';
import { closeOtherModals, registerPanel } from './panels';
import {
  cashAmount, createWindowChrome, goldAmount, windowHeading,
} from './window-chrome';
import { escapeHtml } from '../../util';

/** Narrower than the bag's 5: two grids share the panel's width. */
const TRADE_COLUMNS = 4;
const TRADE_MIN_ROWS = 3;

let unsubscribers: Array<() => void> = [];
let onKeyDown: ((e: KeyboardEvent) => void) | null = null;
let tooltip: ItemTooltip | null = null;
/** Per-stock-entry buy quantity, kept across re-renders (the panel rebuilds wholesale). */
const buyQty = new Map<string, number>();

/**
 * The trade mark — two half-arrows chasing each other round a circle.
 *
 * Inline SVG, following `action-bar.ts`'s `actionIconSvg` precedent: nothing
 * in `assets/icons/` is an exchange mark (checked — 484 files, none matching
 * trade/exchange/arrow/swap), and a one-colour glyph that must take
 * `currentColor` is not worth a raster asset.
 *
 * The "grungy" half is the broken ring: the circle is drawn as four arcs with
 * gaps rather than one stroke, and the whole mark sits at a slight angle, so
 * it reads as stamped and worn rather than as a clean UI affordance.
 */
function tradeMarkSvg(): string {
  return `<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2.2"
    stroke-linecap="round" aria-hidden="true">
    <g transform="rotate(-8 24 24)" opacity="0.75">
      <path d="M24 5.5a18.5 18.5 0 0 1 14.6 7.1" />
      <path d="M41.8 18.4a18.5 18.5 0 0 1 .4 8.4" />
      <path d="M24 42.5a18.5 18.5 0 0 1-14.6-7.1" />
      <path d="M6.2 29.6a18.5 18.5 0 0 1-.4-8.4" />
    </g>
    <path d="M13 19h18l-5-5" stroke-width="2.6" />
    <path d="M35 29H17l5 5" stroke-width="2.6" />
  </svg>`;
}

/** Deregisters this panel from `ui/panels.ts` — see `registerPanel`. */
let unregisterPanel: (() => void) | null = null;
/** Ticks the rotation countdown. See `createVendorUI` for why it patches in place. */
let rotationTimer: number | null = null;
/** Wall-clock ms when the current vendor payload landed — the countdown's origin. */
let rotationFetchedAt = 0;
/** Identity of the payload `rotationFetchedAt` belongs to. */
let lastVendorPayload: VendorState['vendor'] = null;
/** The `rotation.endsAt` we have already fired a refresh for — see the ticker. */
let refreshedForEndsAt: number | null = null;

export function createVendorUI(): HTMLElement {
  // One shell for every window — see `ui/window-chrome.ts`. (`position:
  // relative` on the frame stays load-bearing: the close cross anchors to it,
  // and both `.panel` and `.bv-win` set it.)
  const { root, panel, header, body } = createWindowChrome({
    id: 'vendor-ui',
    headerId: 'vendor-header',
    bodyId: 'vendor-body',
    width: 1000,
    label: 'Close vendor',
    onClose: () => closeVendorUI(),
    bodyClass: 'bv-trade',
  });

  // On document.body: `#vendor-ui` is `position:fixed; z-index:1001`, a
  // stacking context, so a card appended to it can never rise above the player
  // frame at 1002. See ItemTooltip's docstring.
  tooltip = new ItemTooltip();

  // One delegated click handler for both grids — every cell is rebuilt on
  // every render, so per-button listeners would leak with the markup.
  panel.addEventListener('click', onPanelClick);
  panel.addEventListener('change', onQtyChange);
  panel.addEventListener('input', onQtyChange);
  panel.addEventListener('pointerover', onPointerOver);
  panel.addEventListener('pointermove', onPointerMove);
  panel.addEventListener('pointerout', onPointerOut);
  panel.addEventListener('scroll', hideTip, true);

  onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && root.style.display !== 'none') {
      e.preventDefault();
      closeVendorUI();
    }
  };
  document.addEventListener('keydown', onKeyDown);

  const rerender = () => {
    if (root.style.display === 'none') return;
    // A fresh vendor payload restarts the countdown's reference point. Keyed
    // on the object identity `loadVendor` replaces, so a sell/buy that only
    // touches the sheet does not reset the clock.
    const { vendor } = vendorStateStore.get();
    if (vendor && vendor !== lastVendorPayload) {
      lastVendorPayload = vendor;
      rotationFetchedAt = Date.now();
    }
    render(header, body, vendorStateStore.get());
  };
  unsubscribers = [
    vendorStateStore.subscribe(rerender),
    gameStateStore.subscribe(rerender),
  ];

  // Patches the countdown span in place. A full re-render would rebuild the
  // quantity inputs and wipe a half-typed number once a second.
  if (rotationTimer !== null) window.clearInterval(rotationTimer);
  rotationTimer = window.setInterval(() => {
    if (root.style.display === 'none') return;
    const state = vendorStateStore.get();
    if (!state.vendor?.rotation) return;
    const left = rotationRemaining(state.vendor.rotation, rotationFetchedAt, Date.now());
    const span = document.getElementById('vendor-rotation');
    if (span) span.textContent = formatRotation(left);
    // The window turned over while the panel was open: pull the new shelf
    // rather than leaving the player looking at stock they can no longer buy
    // (`buy` re-derives the rotation server-side and would reject it).
    //
    // Throttled on the expiry we last acted on, NOT on `busy`. `loadVendor`'s
    // catch clears `busy` and leaves the stale payload in place — whose
    // `remainingMs` is still expired — so a `!busy` guard alone turns a failing
    // server into one request per second, per open panel, forever.
    if (left <= 0 && !state.busy && refreshedForEndsAt !== state.vendor.rotation.endsAt) {
      refreshedForEndsAt = state.vendor.rotation.endsAt;
      void loadVendor(state.vendor.key);
    }
  }, 1000);


  // One registry instead of the five hand-copied guard chains index.ts
  // used to carry. See ui/panels.ts.
  unregisterPanel?.();
  unregisterPanel = registerPanel({ id: 'vendor', isOpen: isVendorUIOpen, close: closeVendorUI });
  return root;
}

export function openVendorUI(vendorKey: string): void {
  closeOtherModals('vendor');
  const el = document.getElementById('vendor-ui');
  if (el) el.style.display = 'flex';
  buyQty.clear();
  void loadVendor(vendorKey);
}

/** Lets index.ts's `interact()` avoid popping a dialog behind an open vendor panel. */
export function isVendorUIOpen(): boolean {
  const el = document.getElementById('vendor-ui');
  return !!el && el.style.display !== 'none';
}

export function closeVendorUI(): void {
  if (rotationTimer !== null) window.clearInterval(rotationTimer);
  rotationTimer = null;
  const el = document.getElementById('vendor-ui');
  if (el) el.style.display = 'none';
  tooltip?.hide();
  closeVendor();
}

export function disposeVendorUI(): void {
  unregisterPanel?.();
  unregisterPanel = null;
  for (const fn of unsubscribers) fn();
  unsubscribers = [];
  if (rotationTimer !== null) window.clearInterval(rotationTimer);
  rotationTimer = null;
  lastVendorPayload = null;
  refreshedForEndsAt = null;
  // `buyQty` is module-level so a quantity survives the panel's own
  // re-renders; it must NOT survive the panel. Without this the next mount
  // starts with the last session's numbers in inputs that were never typed.
  buyQty.clear();
  if (onKeyDown) document.removeEventListener('keydown', onKeyDown);
  onKeyDown = null;
  tooltip?.dispose();
  tooltip = null;
}

// ─── interaction ────────────────────────────────────────────────────────────

function onPanelClick(e: MouseEvent): void {
  const btn = (e.target as HTMLElement).closest('button[data-action]') as HTMLButtonElement | null;
  if (!btn || btn.disabled) return;
  const { action } = btn.dataset;
  if (action === 'sell' && btn.dataset.itemId) {
    void sellItems([btn.dataset.itemId]);
  } else if (action === 'buy' && btn.dataset.itemKey) {
    const key = btn.dataset.itemKey;
    void buyItem(key, buyQty.get(key) ?? 1);
  }
}

/**
 * Keeps the typed quantity and repaints the line total without a full render.
 *
 * A store-driven re-render would rebuild the input the player is typing in and
 * take the caret with it, so the total is patched in place instead.
 */
function onQtyChange(e: Event): void {
  const input = e.target as HTMLInputElement | null;
  if (!input || input.dataset.qtyFor === undefined) return;
  const key = input.dataset.qtyFor;
  // Two digits, hard. `type=number` ignores `maxlength`, so a paste of "999"
  // has to be clamped here or the player sees a quantity the server rejects.
  // ...and never past what the stall will still sell this player.
  const left = vendorStateStore.get().vendor?.stock.find((x) => x.itemKey === key)?.remaining ?? 99;
  const n = Math.max(1, Math.min(99, Math.max(1, left), Math.floor(Number(input.value) || 1)));
  buyQty.set(key, n);
  if (input.value !== String(n)) input.value = String(n);

  const slot = input.closest('.bv-slot') as HTMLElement | null;
  const unit = Number(slot?.dataset.unitPrice ?? 0);
  const totalEl = slot?.querySelector('[data-line-total]');
  if (totalEl) totalEl.innerHTML = lineTotal(n, unit);
  // Affordability and hideout room both move with the quantity, so the Buy
  // button's gate has to move with it too — otherwise typing 99 leaves a live
  // button that 409s.
  const buy = slot?.querySelector('button[data-action="buy"]') as HTMLButtonElement | null;
  if (buy && slot?.dataset.itemKey) {
    const reason = buyBlockReason(slot.dataset.itemKey, n, vendorStateStore.get());
    buy.disabled = reason !== null;
    buy.title = reason ?? '';
  }
}

function lineTotal(qty: number, unit: number): string {
  return `x ${cashCoinHtml('sm')}${unit * qty}`;
}

function hideTip(): void { tooltip?.hide(); }

function onPointerOver(e: PointerEvent): void {
  const host = (e.target as HTMLElement | null)?.closest('[data-tip]') as HTMLElement | null;
  if (!host?.dataset.tip) return;
  const html = tipHtmlFor(host.dataset.tip);
  if (!html) { tooltip?.hide(); return; }
  tooltip?.show(html, e.clientX, e.clientY);
}

function onPointerMove(e: PointerEvent): void {
  if (!tooltip?.isVisible) return;
  if (!(e.target as HTMLElement | null)?.closest('[data-tip]')) { tooltip.hide(); return; }
  tooltip.move(e.clientX, e.clientY);
}

function onPointerOut(e: PointerEvent): void {
  const from = (e.target as HTMLElement | null)?.closest('[data-tip]');
  if (!from) return;
  // Also fires crossing onto the slot's own buttons and quantity input.
  if ((e.relatedTarget as HTMLElement | null)?.closest('[data-tip]') === from) return;
  tooltip?.hide();
}

/**
 * `data-tip` is `sell:<UserItem.id>` or `buy:<ItemDef.key>` — the prefix says
 * which side the slot is on, which is what decides whether the price line is
 * what the vendor pays or what they charge.
 */
function tipHtmlFor(tipId: string): string | null {
  const state = vendorStateStore.get();
  if (tipId.startsWith('sell:')) {
    const id = tipId.slice(5);
    const item = ownedItems().find((i) => i.id === id);
    if (!item) return null;
    const quote = state.sellQuotes[id];
    const tier = quote && quote.matchTier === 'matched' ? ' (this stall pays extra for these)' : '';
    return inventoryTooltipHtml(item, {
      priceLineHtml: quote ? `Sells for ${cashCoinHtml('sm')}${quote.price}${escapeHtml(tier)}` : undefined,
    });
  }
  if (tipId.startsWith('buy:')) {
    const key = tipId.slice(4);
    const entry = state.vendor?.stock.find((s) => s.itemKey === key);
    if (!entry) return null;
    const qty = buyQty.get(key) ?? 1;
    return itemTooltipHtml({
      name: entry.name,
      kind: entry.kind,
      stats: entry.stats,
      priceLineHtml: (qty > 1
        ? `${cashCoinHtml('sm')}${entry.buyPrice} each · ${cashCoinHtml('sm')}${entry.buyPrice * qty} for ${qty}`
        : `${cashCoinHtml('sm')}${entry.buyPrice}`)
        + ` · ${entry.remaining} of ${entry.limit} left`,
      blockedReason: buyBlockReason(key, qty, state) ?? undefined,
    });
  }
  return null;
}

// ─── capacity + affordability ───────────────────────────────────────────────

function ownedItems(): InventoryItemSummary[] {
  const inv = gameStateStore.get().inventory;
  return [...(inv?.carried ?? []), ...(inv?.hideout ?? [])];
}

/**
 * How many HIDEOUT slots buying `qty` of `itemKey` would actually cost.
 *
 * Mirrors `InventoryService.planWrite`: stackable units top up existing
 * partial stacks for FREE before opening a new row, so a naive
 * `count + qty > cap` wrongly greys out Buy on an ammo top-up the server
 * would happily accept. This is a deliberate duplication of a server rule for
 * the sake of a disabled state; the server stays authoritative and still
 * throws `HIDEOUT_FULL`, so a drift here costs a rejected click, not a wrong
 * outcome.
 */
export function hideoutSlotsNeeded(itemKey: string, qty: number, maxStack: number | null): number {
  if (maxStack == null) return qty;
  const hideout = gameStateStore.get().inventory?.hideout ?? [];
  const room = hideout
    .filter((i) => i.itemId === itemKey && i.maxStack != null)
    .reduce((sum, i) => sum + Math.max(0, (i.maxStack ?? 0) - i.quantity), 0);
  const overflow = Math.max(0, qty - room);
  return Math.ceil(overflow / maxStack);
}

/** Why Buy is dead for this entry and quantity, or null when it is live. */
export function buyBlockReason(itemKey: string, qty: number, state: VendorState): string | null {
  const entry = state.vendor?.stock.find((s) => s.itemKey === itemKey);
  if (!entry) return null;
  if (state.busy) return 'Busy…';
  if (entry.remaining <= 0) return 'Sold out until the next delivery';
  if (qty > entry.remaining) return `Only ${entry.remaining} left for you`;
  const cost = entry.buyPrice * qty;
  const cash = state.balance?.cash;
  if (cash !== undefined && cost > cash) return `Not enough Cash (${cost} needed)`;
  const inv = gameStateStore.get().inventory;
  if (inv) {
    const need = hideoutSlotsNeeded(itemKey, qty, entry.maxStack);
    if (inv.hideoutSlotCount + need > inv.hideoutSlotCap) {
      return need === 1 ? 'Hideout is full' : `Hideout has no room for ${need} more slots`;
    }
  }
  return null;
}

// ─── rendering ──────────────────────────────────────────────────────────────

function render(header: HTMLElement, body: HTMLElement, state: VendorState): void {
  header.innerHTML = '';
  body.innerHTML = '';

  const errLine = state.error ? `<p class="bv-win-err">${escapeHtml(state.error)}</p>` : '';

  if (!state.vendor) {
    header.innerHTML = errLine
      + `<p class="bv-win-empty">${state.busy ? 'Loading…' : 'Vendor unavailable.'}</p>`;
    return;
  }

  // Cash in bone with its unit spelled, Gold in brass behind the coin art —
  // the same pair the HUD, the shop and the banker show. This line read
  // "Cash: 120 · Gold: 3" in plain text, which is the one place a player is
  // deciding which of the two to spend.
  const balance = state.balance
    ? `${cashAmount(state.balance.cash)} · ${goldAmount(state.balance.gold)}`
    : `${cashAmount('—')} · ${goldAmount('—')}`;
  // The restock clock sits UP HERE, beside the trader's own line, and large.
  // Janeks: *"in Trading UI, highlight the Trading timer. Put it above the
  // trading slots, vertically the same place where trader description is and
  // larger."*
  //
  // It was a fragment of the stock block's subtitle — "12 lines · restock in
  // 4h 12m", at 0.72rem, below the sell half and above the grid. That is the
  // one number a returning player opens the stall to read, and it was the
  // smallest text on the screen.
  //
  // The id stays `vendor-rotation` and nothing about the TICKER changes: it
  // patches that span once a second by id, deliberately without re-rendering
  // (a re-render rebuilds the quantity inputs and would wipe a half-typed
  // number). Moving the span moves the clock with it.
  header.innerHTML = errLine + `
    <div class="bv-vendor-head">
      <div class="bv-vendor-head-text">
        ${windowHeading(
    state.vendor.name,
    `Deals in ${state.vendor.category.toLowerCase()} — pays best for matching goods.`,
  )}
      </div>
      ${rotationChip(state)}
    </div>
    <p>${balance}</p>
    ${state.lastReceipt ? `<p class="bv-win-ok">${escapeHtml(state.lastReceipt.message)}</p>` : ''}
  `;

  body.appendChild(sellBlock(state));

  const mark = document.createElement('div');
  mark.className = 'bv-trade-mark';
  mark.setAttribute('aria-hidden', 'true');
  mark.innerHTML = tradeMarkSvg();
  body.appendChild(mark);

  body.appendChild(buyBlock(state));
}

/**
 * The load meter shared by both blocks. `capacity.ts`'s thresholds and the
 * `.bv-cap-*` classes are the same ones the HUD and the paperdoll use, so one
 * table colours every capacity readout in the game.
 */
/**
 * The restock clock, as a plate beside the trader's name.
 *
 * Returns an empty string when the payload carries no rotation — an older
 * server or a cached response. The stock header and the rule notice both
 * already degrade that way, and a chip that rendered "restock in —" would be
 * worse than no chip: it states a fact the client does not have.
 *
 * `aria-live="off"` on purpose. The span underneath it is rewritten once a
 * second, and a polite live region would make a screen reader announce the
 * countdown every second for as long as the stall is open.
 */
function rotationChip(state: VendorState): string {
  const rotation = state.vendor?.rotation;
  if (!rotation) return '';
  const remainingMs = rotationRemaining(rotation, rotationFetchedAt, Date.now());
  const left = formatRotation(remainingMs);
  // Presentation only, all of it CSS-driven so the ticker above stays untouched:
  // the bar drains and the warn/pulse phases fire from animation durations and
  // delays computed here from the SAME remaining value the text uses. Every
  // re-render recomputes them, so a rebuilt chip resumes at the right point.
  const totalMs = Math.max(rotation.hours > 0 ? rotation.hours * 3_600_000 : 0, remainingMs, 1);
  const fromPct = Math.min(100, Math.round((remainingMs / totalMs) * 1000) / 10);
  const warnAt = Math.max(0, remainingMs - totalMs * 0.1);
  const pulseAt = Math.max(0, remainingMs - 10_000);
  const vars = `--rot-from:${fromPct}%;--rot-left:${Math.round(remainingMs)}ms;`
    + `--rot-warn-at:${Math.round(warnAt)}ms;--rot-pulse-at:${Math.round(pulseAt)}ms;`;
  return `<div class="bv-vendor-rot" aria-live="off" style="${vars}">
      <span class="bv-vendor-rot-label">Restock in</span>
      <span class="bv-vendor-rot-value" id="vendor-rotation">${escapeHtml(left)}</span>
      <span class="bv-vendor-rot-bar" aria-hidden="true"><span class="bv-vendor-rot-fill"></span></span>
    </div>`;
}

function loadMeter(label: string, used: number, cap: number, text: string, level: string): HTMLElement {
  const box = document.createElement('div');
  box.className = `bv-load bv-cap-${level}`;
  box.innerHTML = `<div class="bv-load-head">
      <span class="bv-load-label">${label}</span>
      <span class="bv-load-slots">${used} / ${cap} slots</span>
    </div>
    <div class="bv-load-bar">
      <div class="bv-load-fill" style="width:${fillPercent(used, cap)}%"></div>
      <span class="bv-load-text">${text}</span>
    </div>`;
  return box;
}

function block(title: string, sub: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'bv-block';
  el.innerHTML = `<div class="bv-block-head">
      <span class="bv-block-title">${title}</span>
      <span class="bv-block-sub">${sub}</span>
    </div>`;
  return el;
}

function grid(cells: string[]): HTMLElement {
  const el = document.createElement('div');
  el.className = 'bv-slots';
  el.style.cssText = slotGridStyle(TRADE_COLUMNS);
  el.innerHTML = cells.join('');
  return el;
}

function sellBlock(state: VendorState): HTMLElement {
  const items = ownedItems();
  const inv = gameStateStore.get().inventory;
  const col = block('Your goods', `${items.length} to trade`);
  col.dataset.side = 'sell';

  if (inv) {
    // Weight AND slots, coloured by whichever is under more pressure — the
    // carried bag is blocked by either (inventory.md rule 3), so a meter that
    // tracked only weight would look healthy on a slot-blocked bag.
    const level = worstLevel(
      capacityLevel(inv.carriedWeight, inv.carryWeightCap),
      capacityLevel(inv.carriedSlotCount, inv.carriedSlotCap),
    );
    col.appendChild(loadMeter(
      'Load',
      inv.carriedSlotCount,
      inv.carriedSlotCap,
      `${inv.carriedWeight.toFixed(1)}/${inv.carryWeightCap.toFixed(1)} kg`,
      level,
    ));
  }

  const total = gridSlotCount(items.length, TRADE_COLUMNS, TRADE_MIN_ROWS);
  const cells: string[] = [];
  for (let i = 0; i < total; i += 1) {
    const item = items[i];
    cells.push(item ? sellSlot(item, state) : emptySlotHtml());
  }
  col.appendChild(grid(cells));

  if (items.length === 0) {
    // The grid still renders (that is the ask); this says why it is empty,
    // under it rather than instead of it.
    const note = document.createElement('p');
    note.className = 'bv-block-note';
    note.textContent = 'Nothing to sell.';
    col.appendChild(note);
  }
  return col;
}

function sellSlot(item: InventoryItemSummary, state: VendorState): string {
  const quote = state.sellQuotes[item.id];
  const actions: SlotAction[] = [{
    action: 'sell',
    label: 'Sell',
    variant: 'go',
    disabled: state.busy,
  }];
  return slotHtml({
    itemId: item.id,
    name: item.name,
    iconPath: item.iconPath,
    quantity: item.quantity,
    // The price the SERVER would pay, not a client guess — see the module
    // docstring. Absent until the quote lands, rather than shown as 0.
    priceHtml: quote ? `${cashCoinHtml('sm')}${quote.price}` : undefined,
    tipId: `sell:${item.id}`,
    actions,
  }, pictureHtml(item.iconPath, item.name, '', '88px', { kind: item.kind, slot: item.slot, key: item.itemId }));
}

/**
 * Formats a rotation countdown as `4h 12m` / `12m 30s` / `30s`.
 *
 * Seconds are only shown under an hour: a shelf that turns over every 15
 * hours does not need a ticking second hand for most of its life, and a
 * number that changes every second reads as urgent when it is not.
 */
export function formatRotation(remainingMs: number): string {
  const s = Math.max(0, Math.floor(remainingMs / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

/**
 * Ms left in the current window, measured from the server's own
 * `remainingMs` plus elapsed wall time since the payload landed.
 *
 * NOT `endsAt - Date.now()`: the two differ by whatever the player's clock is
 * off by, and the countdown is the one number the panel asks them to trust.
 * `Date.now()` is still what measures ELAPSED time here, which is fine — a
 * clock that is wrong by hours still ticks at one second per second.
 */
export function rotationRemaining(
  rotation: { remainingMs: number },
  fetchedAtMs: number,
  nowMs: number,
): number {
  return Math.max(0, rotation.remainingMs - (nowMs - fetchedAtMs));
}

function buyBlock(state: VendorState): HTMLElement {
  const stock = state.vendor?.stock ?? [];
  const inv = gameStateStore.get().inventory;
  const rotation = state.vendor?.rotation;
  // Just the count now. The countdown moved up to the header plate — see
  // `rotationChip` — because it is what the player opened the stall to read
  // and it was the smallest text in the panel.
  const col = block('Stock', `${stock.length} lines`);
  col.dataset.side = 'buy';

  if (inv) {
    col.appendChild(loadMeter(
      'Hideout',
      inv.hideoutSlotCount,
      inv.hideoutSlotCap,
      `${inv.hideoutSlotCount}/${inv.hideoutSlotCap}`,
      capacityLevel(inv.hideoutSlotCount, inv.hideoutSlotCap),
    ));
  }

  const total = gridSlotCount(stock.length, TRADE_COLUMNS, TRADE_MIN_ROWS);
  const cells: string[] = [];
  for (let i = 0; i < total; i += 1) {
    const entry = stock[i];
    cells.push(entry ? buySlot(entry, state) : emptySlotHtml());
  }
  col.appendChild(grid(cells));

  if (stock.length === 0) {
    const note = document.createElement('p');
    note.className = 'bv-block-note';
    note.textContent = 'Nothing in stock.';
    col.appendChild(note);
  }

  // The notice. Says the rule ("every 15h") as well as the countdown, because
  // a bare timer does not tell a first-time player what is about to happen —
  // and names the next level that widens the shelf, so the progression is
  // legible rather than something they might notice months later.
  // Guarded on `rotation`, exactly as the header above is: the header already
  // decided a payload without one is possible (an older server, a cached
  // response), and a notice that throws where the header degrades would take
  // the whole panel down instead.
  if (state.vendor && rotation) {
    const note = document.createElement('p');
    note.className = 'bv-block-note';
    const next = state.vendor.nextUnlockLevel;
    note.textContent = `Stock rotates every ${rotation.hours}h.`
      + (next != null ? ` More lines unlock at level ${next}.` : ' You see every line this stall carries.');
    col.appendChild(note);
  }
  return col;
}

/**
 * A stock slot. The quantity input and the line total sit on ONE row inside
 * the hover overlay, reading "[2] x 30 Cash" — the request's own shape. The
 * input is 2 digits wide and clamped to 2 digits, and the total is patched in
 * place as it changes rather than triggering a re-render that would rebuild
 * the input mid-keystroke.
 */
function buySlot(entry: VendorStockEntry, state: VendorState): string {
  const qty = buyQty.get(entry.itemKey) ?? 1;
  const blocked = buyBlockReason(entry.itemKey, qty, state);
  const actions: SlotAction[] = [{
    action: 'buy',
    label: 'Buy',
    variant: 'buy',
    disabled: blocked !== null,
    title: blocked ?? undefined,
  }];

  const html = slotHtml({
    itemKey: entry.itemKey,
    name: entry.name,
    iconPath: entry.iconPath,
    priceHtml: `${cashCoinHtml('sm')}${entry.buyPrice}`,
    stockLabel: entry.remaining > 0 ? `${entry.remaining} left` : 'Sold out',
    tipId: `buy:${entry.itemKey}`,
    actions,
  }, pictureHtml(entry.iconPath, entry.name, '', '88px', { kind: entry.kind, key: entry.itemKey }));

  const qtyRow = `<div class="bv-slot-qtyrow">
      <input type="number" class="bv-slot-qtyinput" min="1" max="${Math.max(1, Math.min(99, entry.remaining))}" inputmode="numeric"
        data-qty-for="${entry.itemKey}" value="${qty}" aria-label="Quantity to buy" />
      <span class="bv-slot-linetotal" data-line-total>${lineTotal(qty, entry.buyPrice)}</span>
    </div>`;

  // Injected into the overlay rather than composed by `slotHtml`: the quantity
  // control is specific to buying, and teaching the shared slot renderer about
  // it would put a trade concern in the module the bag also uses.
  return html
    .replace('<div class="bv-slot-actions">', `<div class="bv-slot-actions">${qtyRow}`)
    .replace('<div class="bv-slot bv-slot-filled"', `<div class="bv-slot bv-slot-filled" data-unit-price="${entry.buyPrice}"`);
}

